import React from 'react';
import { Typography, Box, ThemeProvider } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { QRCodeCard, QRCode, StoreImgWrapper } from './DownloadSparkApp.styles';

import { getPlatform } from '@/pages/Onboarding/utils/platformHelpers';
import { Platform } from '@/utils/productHelpers';
import {
  SPARK_APP_STORE_URL,
  SPARK_GOOGLE_PLAY_URL,
  SPARK_SMART_LINK_URL,
} from '@/pages/Onboarding/utils/constants';
import { ContentContainer } from '@/pages/PreOnboarding/steps/AccessCode/AccessCodeEntry.styles';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import sparkTheme from '@/themes/sparkTheme';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { useConsent } from '@/hooks/useConsent';
import LimbotHappy from '@/assets/LimbotHappy.svg';
import AppStore from '@/assets/AppStore.svg';
import GooglePlay from '@/assets/GooglePlay.svg';

const DownloadSparkAppInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();

  const { t } = useTranslation();
  const platform = getPlatform();

  const renderPlatformContent = () => {
    if (platform === Platform.iOS) {
      return (
        <StoreImgWrapper href={SPARK_APP_STORE_URL} data-testid="app-store-link">
          <img alt="App Store" src={AppStore} />
        </StoreImgWrapper>
      );
    }

    if (platform === Platform.Android) {
      return (
        <StoreImgWrapper href={SPARK_GOOGLE_PLAY_URL} data-testid="google-play-link">
          <img alt="Google Play" src={GooglePlay} />
        </StoreImgWrapper>
      );
    }

    return (
      <QRCodeCard data-testid="qr-code-card">
        <QRCode
          data-testid="qr-code"
          value={SPARK_SMART_LINK_URL}
          size={120}
        />
        <Box>
          <Typography
            style={{ fontWeight: 700, marginBottom: 16 }}
            color="textPrimary"
          >
            {t('coverage.spark.download_app.qr_title')}
          </Typography>
          <Typography color="textPrimary">
            {t('coverage.spark.download_app.qr_subtitle')}
          </Typography>
        </Box>
      </QRCodeCard>
    );
  };

  return (
    <ContentContainer>
      <Header />
      <Box style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        flex: 1,
        padding: '0 24px',
      }}
      >
        <Box style={{ paddingTop: 32, paddingBottom: 16 }}>
          <img
            alt="Limbot Happy"
            style={{ width: 206 }}
            src={LimbotHappy}
          />
        </Box>
        <Typography variant="h2" color="textPrimary" style={{ marginBottom: 8 }}>
          {t('coverage.spark.download_app.title')}
        </Typography>
        <Typography color="textPrimary" style={{ marginBottom: 24, maxWidth: 653 }}>
          {t('coverage.spark.download_app.subtitle')}
        </Typography>
        {renderPlatformContent()}
      </Box>
      <Footer />
    </ContentContainer>
  );
};

const DownloadSparkApp: React.FC = () => (
  <ThemeProvider theme={sparkTheme}>
    <DownloadSparkAppInner />
  </ThemeProvider>
);

export default DownloadSparkApp;
